import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { api } from '@/lib/api';
import type { SystemState } from '@market-buddy/shared';

/**
 * One slim, honest line about what the system is doing right now, on every
 * page. Driven by GET /api/admin/status (data.system) — the same consolidated
 * truth Settings and the Activity page's Background Monitor use, so surfaces
 * can no longer contradict each other.
 */

interface AdminStatusResponse {
  system?: SystemState;
}

export type BannerTone = 'warning' | 'neutral' | 'success';

export interface BannerContent {
  /** Stable key per state kind — dismissing one state won't hide the next. */
  key: string;
  tone: BannerTone;
  text: string;
}

function formatUtcShortDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function formatUtcDayTime(ts: number): string {
  const d = new Date(ts);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${day} ${time}`;
}

export function deriveBanner(s: SystemState): BannerContent {
  const marketNote =
    s.marketState === 'weekend'
      ? 'market closed (weekend)'
      : s.marketState === 'closed'
        ? 'market closed'
        : null;

  if (!s.autoTrade.enabled) {
    const since = s.autoTrade.since ? ` since ${formatUtcShortDate(s.autoTrade.since)}` : '';
    const tail = s.pollingEnabled
      ? 'safety exits still managing open positions'
      : 'polling also off — no new data';
    return {
      key: 'paused-autotrade',
      tone: 'warning',
      text: `PAUSED — auto-trade off${since}; ${tail}${marketNote ? ` · ${marketNote}` : ''}`,
    };
  }

  if (!s.pollingEnabled) {
    return {
      key: 'paused-polling',
      tone: 'warning',
      text: 'PAUSED — market-data polling off; no new scans until re-enabled',
    };
  }

  if (s.marketState === 'weekend') {
    return {
      key: 'weekend',
      tone: 'neutral',
      text: "MARKET CLOSED — weekend; data as of Friday's close",
    };
  }

  if (s.marketState === 'closed') {
    return {
      key: 'closed',
      tone: 'neutral',
      text: `MARKET CLOSED — next scan window ${formatUtcDayTime(s.nextCronRun)} UTC`,
    };
  }

  return { key: 'live', tone: 'success', text: 'LIVE — scanning every 5 min' };
}

const toneTokens: Record<BannerTone, { bg: string; color: string; borderColor: string }> = {
  warning: { bg: 'status.warning.bg', color: 'status.warning.text', borderColor: 'status.warning.border' },
  neutral: { bg: 'status.neutral.bg', color: 'status.neutral.text', borderColor: 'status.neutral.border' },
  success: { bg: 'status.success.bg', color: 'status.success.text', borderColor: 'status.success.border' },
};

const DISMISS_PREFIX = 'mb-system-banner-dismissed:';

export function SystemStateBanner() {
  const [system, setSystem] = useState<SystemState | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const fetchState = useCallback(async () => {
    try {
      const data = await api.get<AdminStatusResponse>('/api/admin/status');
      if (data.system) setSystem(data.system);
    } catch {
      // No status — show nothing rather than something wrong.
    }
  }, []);

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, 60_000);
    return () => clearInterval(id);
  }, [fetchState]);

  if (!system) return null;

  const banner = deriveBanner(system);
  const alreadyDismissed =
    dismissedKey === banner.key || sessionStorage.getItem(DISMISS_PREFIX + banner.key) === '1';
  if (alreadyDismissed) return null;

  const tone = toneTokens[banner.tone];

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="3"
      px="4"
      py="2"
      mb="4"
      borderRadius="md"
      border="1px solid"
      bg={tone.bg}
      color={tone.color}
      borderColor={tone.borderColor}
    >
      <Text fontSize="13px" lineHeight="18px" fontWeight="500" m={0} truncate>
        {banner.text}
      </Text>
      <Box
        as="button"
        onClick={() => {
          sessionStorage.setItem(DISMISS_PREFIX + banner.key, '1');
          setDismissedKey(banner.key);
        }}
        aria-label="Dismiss system status banner"
        title="Dismiss for this session"
        bg="transparent"
        border="none"
        color="inherit"
        cursor="pointer"
        fontSize="14px"
        lineHeight="1"
        p="1"
        flexShrink={0}
        opacity={0.7}
        _hover={{ opacity: 1 }}
      >
        &#10005;
      </Box>
    </Flex>
  );
}
